import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Grid, IconButton, useTheme } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SparklesIcon from '@mui/icons-material/AutoAwesome';

const initialCategories = [
    { name: 'Man (People)', icon: '👤', causes: [] },
    { name: 'Machine (Equipment)', icon: '⚙️', causes: [] },
    { name: 'Method (Process)', icon: '📋', causes: [] },
    { name: 'Material', icon: '📦', causes: [] },
    { name: 'Measurement', icon: '📏', causes: [] },
    { name: 'Environment', icon: '🌍', causes: [] }
];

const mockSuggestions = {
    'Man (People)': [
        'Inadequate training',
        'Lack of experience',
        'Fatigue or stress',
    ],
    'Machine (Equipment)': [
        'Equipment malfunction',
        'Lack of maintenance',
        'Outdated technology',
    ],
    'Method (Process)': [
        'Unclear procedures',
        'Process not standardized',
        'Inefficient workflow',
    ],
    'Material': [
        'Poor quality materials',
        'Incorrect specifications',
        'Supplier issues',
    ],
    'Measurement': [
        'Inaccurate measurements',
        'Calibration issues',
        'Wrong measurement tools',
    ],
    'Environment': [
        'Temperature variations',
        'Humidity issues',
        'Poor lighting',
    ]
};

export default function FishboneAnalysis() {
    const theme = useTheme();
    const [problemStatement, setProblemStatement] = useState('');
    const [categories, setCategories] = useState(initialCategories);

    const addCause = (categoryIndex) => {
        const newCategories = [...categories];
        const newId = newCategories[categoryIndex].causes.length > 0
            ? Math.max(...newCategories[categoryIndex].causes.map(c => c.id)) + 1
            : 1;
        newCategories[categoryIndex].causes.push({ id: newId, text: '' });
        setCategories(newCategories);
    };

    const updateCause = (categoryIndex, causeId, text) => {
        const newCategories = [...categories];
        const causeIndex = newCategories[categoryIndex].causes.findIndex(c => c.id === causeId);
        if (causeIndex !== -1) {
            newCategories[categoryIndex].causes[causeIndex].text = text;
            setCategories(newCategories);
        }
    };

    const removeCause = (categoryIndex, causeId) => {
        const newCategories = [...categories];
        newCategories[categoryIndex].causes = newCategories[categoryIndex].causes.filter(c => c.id !== causeId);
        setCategories(newCategories);
    };

    const aiSuggest = (categoryIndex) => {
        const categoryName = categories[categoryIndex].name;
        const categorySuggestions = mockSuggestions[categoryName] || [];

        if (categorySuggestions.length === 0) return;

        const existingTexts = categories[categoryIndex].causes.map(c => c.text);
        const availableSuggestions = categorySuggestions.filter(s => !existingTexts.includes(s));

        if (availableSuggestions.length === 0) return;

        const randomSuggestion = availableSuggestions[Math.floor(Math.random() * availableSuggestions.length)];

        const newCategories = [...categories];
        const newId = newCategories[categoryIndex].causes.length > 0
            ? Math.max(...newCategories[categoryIndex].causes.map(c => c.id)) + 1
            : 1;

        newCategories[categoryIndex].causes.push({ id: newId, text: randomSuggestion });
        setCategories(newCategories);
    };

    const generateReport = () => {
        const reportContent = `
FISHBONE DIAGRAM (ISHIKAWA) ANALYSIS REPORT
Generated: ${new Date().toLocaleString()}
 
PROBLEM STATEMENT:
${problemStatement || 'Not specified'}
 
CAUSE ANALYSIS BY CATEGORY:
${categories.map(category => `
${category.icon} ${category.name.toUpperCase()}:
${category.causes.length === 0 ? '  - No causes identified' : category.causes.map(cause => `  - ${cause.text}`).join('\n')}
`).join('\n')}
    `.trim();

        const blob = new Blob([reportContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fishbone-analysis-${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const totalCauses = categories.reduce((sum, cat) => sum + cat.causes.length, 0);

    return (
        <Box sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5 }}>
                    Problem Statement (Effect)
                </Typography>
                <TextField
                    multiline
                    rows={3}
                    value={problemStatement}
                    onChange={(e) => setProblemStatement(e.target.value)}
                    placeholder="Describe the problem or effect you want to analyze..."
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: 'grey.300',
                            transition: 'all 0.3s',
                            '&:hover fieldset': { borderColor: 'primary.main' },
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                boxShadow: '0 0 0 4px rgba(66, 133, 244, 0.2)',
                                borderWidth: '2px',
                            },
                            '& fieldset': { border: 'none' },
                        },
                    }}
                />
            </Box>

            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>
                    6M Categories - Identify Potential Causes
                </Typography>
                <Grid container spacing={3}>
                    {categories.map((category, categoryIndex) => (
                        <Grid item xs={12} md={6} key={categoryIndex}>
                            <Box
                                sx={{
                                    background: 'linear-gradient(to bottom right, #F8FAFC, white)',
                                    borderRadius: 3,
                                    p: 3,
                                    border: '2px solid',
                                    borderColor: 'grey.200',
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        borderColor: '#93C5FD',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box component="span" sx={{ fontSize: '1.5rem' }}>{category.icon}</Box>
                                        {category.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <IconButton
                                            onClick={() => aiSuggest(categoryIndex)}
                                            title="AI Suggestion"
                                            sx={{
                                                background: 'linear-gradient(to right, #A855F7, #EC4899)',
                                                color: 'white',
                                                p: 1,
                                                boxShadow: theme.shadows[3],
                                                '&:hover': {
                                                    background: 'linear-gradient(to right, #9333EA, #DB2777)',
                                                    boxShadow: theme.shadows[6],
                                                },
                                            }}
                                        >
                                            <SparklesIcon sx={{ width: 16, height: 16 }} />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => addCause(categoryIndex)}
                                            sx={{
                                                background: 'linear-gradient(to right, #06B6D4, #2563EB)',
                                                color: 'white',
                                                p: 1,
                                                '&:hover': {
                                                    background: 'linear-gradient(to right, #0891B2, #1D4ED8)',
                                                },
                                            }}
                                        >
                                            <AddIcon sx={{ width: 16, height: 16 }} />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {category.causes.length === 0 ? (
                                        <Typography variant="body2" sx={{ color: 'grey.400', fontStyle: 'italic' }}>No causes added yet</Typography>
                                    ) : (
                                        category.causes.map((cause) => (
                                            <Box key={cause.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TextField
                                                    value={cause.text}
                                                    onChange={(e) => updateCause(categoryIndex, cause.id, e.target.value)}
                                                    placeholder="Enter a cause..."
                                                    fullWidth
                                                    size="small"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 1.5,
                                                        }
                                                    }}
                                                />
                                                <IconButton
                                                    onClick={() => removeCause(categoryIndex, cause.id)}
                                                    sx={{ color: 'error.main', '&:hover': { backgroundColor: 'red.50' } }}
                                                    aria-label="Remove cause"
                                                >
                                                    <DeleteIcon sx={{ width: 18, height: 18 }} />
                                                </IconButton>
                                            </Box>
                                        ))
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>


            {totalCauses > 0 && (
                <Box
                    sx={{
                        background: 'linear-gradient(to right, #ECFEFF, #EFF6FF)',
                        border: '2px solid #A5F3FC',
                        borderRadius: 2,
                        p: 2,
                        mb: 3,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>Analysis Progress</Typography>
                            <Typography variant="body2" sx={{ color: 'grey.600' }}>
                                {totalCauses} potential cause{totalCauses !== 1 ? 's' : ''} identified across {categories.filter(c => c.causes.length > 0).length} categories
                            </Typography>
                        </Box>
                        <Typography sx={{
                            fontSize: '1.875rem',
                            fontWeight: 700,
                            background: 'linear-gradient(to right, #06B6D4, #2563EB)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textFillColor: 'transparent',
                        }}
                        >
                            {totalCauses}
                        </Typography>
                    </Box>
                </Box>
            )}

            <Button
                onClick={generateReport}
                disabled={!problemStatement || totalCauses === 0}
                fullWidth
                sx={{
                    background: 'linear-gradient(to right, #10B981, #059669)',
                    color: 'white',
                    px: 3,
                    py: 2,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    transition: 'all 0.3s',
                    boxShadow: theme.shadows[6],
                    '&:hover': {
                        background: 'linear-gradient(to right, #059669, #047857)',
                        boxShadow: theme.shadows[10],
                    },
                    '&.Mui-disabled': {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                        color: 'white',
                    },
                }}
                startIcon={<DownloadIcon sx={{ width: 20, height: 20 }} />}
            >
                Generate Instant Report
            </Button>

            {problemStatement && totalCauses > 0 && (
                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        background: 'linear-gradient(to right, #EFF6FF, #ECFEFF)',
                        borderRadius: 2,
                        border: '1px solid #BFDBFE',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        <Box component="strong" sx={{ color: 'text.primary' }}>💡 Tip:</Box> Review all identified causes, prioritize them by impact, and develop targeted corrective actions for the most critical ones.
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
